import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { Alert } from '@serps/ui-kit/src/components/Alert';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface AppDetail {
  id:                   string;
  application_number:   string;
  status:               string;
  applied_level:        string | null;
  student_full_name_en: string;
  student_full_name_ms: string | null;
  student_ic_number:    string | null;
  student_dob:          string;
  student_gender:       string;
  student_nationality:  string;
  student_religion:     string | null;
  student_address:      string | null;
  student_district:     string | null;
  student_postcode:     string | null;
  guardian_full_name:   string;
  guardian_ic:          string;
  guardian_phone:       string;
  guardian_email:       string | null;
  guardian_relationship:string;
  school_name:          string | null;
  school_code:          string | null;
  eligibility_check:    Record<string, unknown> | null;
  submitted_at:         string | null;
  reviewed_at:          string | null;
  rejection_reason:     string | null;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft:          { label: 'Draf / Draft',                    color: colors.neutral.gray400, bg: 'rgba(150,150,150,0.1)' },
  submitted:      { label: 'Dihantar / Submitted',            color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  pending_review: { label: 'Menunggu Semakan / Pending Review',color: '#facc15', bg: 'rgba(250,204,21,0.1)'  },
  approved:       { label: 'Diluluskan / Approved',           color: '#4ade80', bg: 'rgba(74,222,128,0.1)'  },
  rejected:       { label: 'Ditolak / Rejected',              color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
  withdrawn:      { label: 'Ditarik Balik / Withdrawn',       color: colors.neutral.gray600, bg: 'rgba(100,100,100,0.1)' },
};

export function ApplicationDetailPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === "en";
  const { id } = useParams<{ id: string }>();
  const [app,     setApp]     = useState<AppDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason,  setRejectReason]  = useState('');
  const [overrideReason,setOverrideReason]= useState('');
  const [showRejectForm,setShowRejectForm]= useState(false);
  const [toast,         setToast]         = useState<{ message: string; variant: ToastVariant } | null>(null);

  const isSuperAdmin = user.roles.some((r) => r.role === 'super_admin' && r.schoolId === null);
  const canReview    = isSuperAdmin || user.roles.some((r) => ['admin','principal'].includes(r.role));

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    apiGet<AppDetail>(`/api/students/applications/${id}`)
      .then(setApp)
      .catch((err) => {
        const msg = err instanceof TypeError && err.message.includes('fetch')
          ? 'Tidak dapat menghubungi pelayan. / Cannot reach server.'
          : err instanceof Error ? err.message : 'Ralat tidak diketahui.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [id]);

  async function handleApprove() {
    if (!id || actionLoading) return;
    setActionLoading(true);
    try {
      const result = await apiPost<{ success: boolean; studentNumber: string }>(
        `/api/students/applications/${id}/approve`,
        overrideReason ? { overrideReason } : {},
      );
      setToast({
        variant: 'success',
        message: `Diluluskan! No. Pelajar: ${result.studentNumber} / Approved! Student No: ${result.studentNumber}`,
      });
      // Refresh
      const updated = await apiGet<AppDetail>(`/api/students/applications/${id}`);
      setApp(updated);
      setOverrideReason('');
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Gagal meluluskan. / Approval failed.',
      });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!id || actionLoading) return;
    if (!rejectReason.trim()) {
      setToast({ variant: 'error', message: 'Sila masukkan sebab penolakan. / Please enter a rejection reason.' });
      return;
    }
    if (rejectReason.trim().length < 10) {
      setToast({ variant: 'error', message: 'Sebab penolakan mesti sekurang-kurangnya 10 aksara. / Rejection reason must be at least 10 characters.' });
      return;
    }
    setActionLoading(true);
    try {
      await apiPost(`/api/students/applications/${id}/reject`, { rejectionReason: rejectReason.trim() });
      setToast({ variant: 'success', message: 'Permohonan telah ditolak. / Application has been rejected.' });
      const updated = await apiGet<AppDetail>(`/api/students/applications/${id}`);
      setApp(updated);
      setShowRejectForm(false);
      setRejectReason('');
    } catch (err) {
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Gagal menolak. / Rejection failed.',
      });
    } finally {
      setActionLoading(false);
    }
  }

  const isPending = app?.status === 'pending_review' || app?.status === 'submitted';
  const statusCfg = STATUS_CONFIG[app?.status ?? ''] ?? { label: app?.status ?? '', color: colors.neutral.gray400, bg: 'rgba(150,150,150,0.1)' };

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>

          {/* Nav */}
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/admin/applications" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>
              ← Senarai Permohonan / Back to Applications
            </Link>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : !app ? null : (
            <>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.md, marginBottom: spacing.xl, flexWrap: 'wrap' }}>
                <div>
                  <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                    {app.application_number}
                  </h1>
                  <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                    {app.submitted_at ? `Dihantar: ${new Date(app.submitted_at).toLocaleDateString('ms-BN')}` : 'Belum dihantar'}
                  </p>
                </div>
                <span style={{
                  display: 'inline-block', padding: `${spacing.xs} ${spacing.md}`,
                  backgroundColor: statusCfg.bg, color: statusCfg.color,
                  borderRadius: borderRadius.full, fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold, flexShrink: 0,
                }}>
                  {statusCfg.label}
                </span>
              </div>

              {/* Rejection notice */}
              {app.status === 'rejected' && app.rejection_reason && (
                <div style={{ marginBottom: spacing.lg, padding: spacing.md, backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: borderRadius.lg }}>
                  <p style={{ margin: `0 0 ${spacing.xs}`, fontWeight: typography.fontWeight.semibold, color: '#f87171', fontSize: typography.fontSize.sm }}>Sebab Penolakan / Rejection Reason</p>
                  <p style={{ margin: 0, color: colors.neutral.gray200, fontSize: typography.fontSize.sm }}>{app.rejection_reason}</p>
                </div>
              )}

              {/* Eligibility check */}
              {app.eligibility_check && (
                <EligibilityPanel check={app.eligibility_check} />
              )}

              {/* Details grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.xl }}>
                <Section title="Maklumat Pelajar / Student">
                  <Field label="Nama (EN)"     value={app.student_full_name_en} />
                  {app.student_full_name_ms && <Field label="Nama (MS)"     value={app.student_full_name_ms} />}
                  <Field label="No. IC"        value={app.student_ic_number} />
                  <Field label="Tarikh Lahir"  value={app.student_dob ? new Date(app.student_dob).toLocaleDateString('ms-BN') : null} />
                  <Field label="Jantina"       value={app.student_gender} />
                  <Field label="Kewarganegaraan" value={app.student_nationality} />
                  <Field label="Agama"         value={app.student_religion} />
                  <Field label="Aras Dipohon"  value={app.applied_level} />
                </Section>

                <Section title="Maklumat Wali / Guardian">
                  <Field label="Nama"          value={app.guardian_full_name} />
                  <Field label="No. IC"        value={app.guardian_ic} />
                  <Field label="Hubungan"      value={app.guardian_relationship} />
                  <Field label="Telefon"       value={app.guardian_phone} />
                  <Field label="Emel"          value={app.guardian_email} />
                </Section>
              </div>

              {(app.student_address || app.school_name) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.xl }}>
                  <Section title="Alamat / Address">
                    <Field label="Alamat"    value={app.student_address} />
                    <Field label="Daerah"    value={app.student_district} />
                    <Field label="Poskod"    value={app.student_postcode} />
                  </Section>
                  <Section title="Sekolah / School">
                    <Field label="Sekolah Dipilih" value={app.school_name} />
                    <Field label="Kod Sekolah"      value={app.school_code} />
                  </Section>
                </div>
              )}

              {/* Review actions */}
              {canReview && isPending && (
                <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, boxShadow: shadows.xl }}>
                  <h3 style={{ margin: `0 0 ${spacing.lg}`, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                    Tindakan Semakan / Review Actions
                  </h3>

                  {/* Approve */}
                  {!showRejectForm && (
                    <div style={{ marginBottom: spacing.md }}>
                      {app.eligibility_check && (app.eligibility_check as { overall?: string }).overall === 'review_required' && (
                        <div style={{ marginBottom: spacing.sm }}>
                          <label style={{ display: 'block', fontSize: typography.fontSize.sm, color: colors.neutral.gray400, marginBottom: spacing.xs }}>
                            Sebab Pengecualian (diperlukan kerana semakan kelayakan) / Override reason (required due to eligibility flags)
                          </label>
                          <textarea
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="Nyatakan sebab pengecualian... / State override reason..."
                            disabled={actionLoading}
                            rows={2}
                            style={textareaStyle()}
                          />
                        </div>
                      )}
                      <Button
                        variant="primary" size="md"
                        onClick={handleApprove}
                        disabled={actionLoading || (
                          (app.eligibility_check as { overall?: string } | null)?.overall === 'review_required' && !overrideReason.trim()
                        )}
                        loading={actionLoading}
                      >
                        ✓ Luluskan Permohonan / Approve Application
                      </Button>
                    </div>
                  )}

                  {/* Reject toggle */}
                  {!showRejectForm ? (
                    <Button variant="danger" size="md" onClick={() => setShowRejectForm(true)} disabled={actionLoading}>
                      ✕ Tolak Permohonan / Reject Application
                    </Button>
                  ) : (
                    <div style={{ borderTop: `1px solid ${colors.neutral.gray800}`, paddingTop: spacing.md }}>
                      <label style={{ display: 'block', fontSize: typography.fontSize.sm, color: colors.neutral.gray400, marginBottom: spacing.xs }}>
                        Sebab Penolakan * / Rejection Reason * (min 10 aksara / chars)
                      </label>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Nyatakan sebab penolakan... / State rejection reason..."
                        disabled={actionLoading}
                        rows={3}
                        style={textareaStyle()}
                      />
                      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.sm }}>
                        <Button variant="danger" size="md" onClick={handleReject} disabled={actionLoading || rejectReason.trim().length < 10} loading={actionLoading}>
                          Sahkan Penolakan / Confirm Rejection
                        </Button>
                        <Button variant="ghost" size="md" onClick={() => { setShowRejectForm(false); setRejectReason(''); }} disabled={actionLoading}>
                          Batal / Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Approved summary */}
              {app.status === 'approved' && app.reviewed_at && (
                <div style={{ padding: spacing.md, backgroundColor: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: borderRadius.lg }}>
                  <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: '#4ade80' }}>
                    ✓ Diluluskan pada {new Date(app.reviewed_at).toLocaleDateString('ms-BN')} / Approved on {new Date(app.reviewed_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {toast && (
        <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.lg, padding: spacing.md }}>
      <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: spacing.xs }}>
      <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{label}: </span>
      <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.white }}>{value}</span>
    </div>
  );
}

function EligibilityPanel({ check }: { check: Record<string, unknown> }) {
  const overall = (check['overall'] as string) ?? 'unknown';
  const checks  = (check['checks'] as Record<string, { passed: boolean; reason?: string; ageYears?: number }>) ?? {};
  const color   = overall === 'eligible' ? '#4ade80' : overall === 'review_required' ? '#facc15' : '#f87171';

  return (
    <div style={{ marginBottom: spacing.lg, padding: spacing.md, backgroundColor: `${color}11`, border: `1px solid ${color}44`, borderRadius: borderRadius.lg }}>
      <p style={{ margin: `0 0 ${spacing.sm}`, fontWeight: typography.fontWeight.semibold, color, fontSize: typography.fontSize.sm }}>
        Semakan Kelayakan: {overall.replace('_', ' ').toUpperCase()} / Eligibility Check
      </p>
      {Object.entries(checks).map(([key, val]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, marginBottom: '2px' }}>
          <span style={{ color: val.passed ? '#4ade80' : '#f87171', fontSize: '12px' }}>{val.passed ? '✓' : '✕'}</span>
          <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
            {key.replace(/_/g, ' ')}
            {val.ageYears !== undefined && ` (${val.ageYears} tahun/years)`}
            {!val.passed && val.reason && ` — ${val.reason}`}
          </span>
        </div>
      ))}
    </div>
  );
}

function textareaStyle(): React.CSSProperties {
  return {
    width:           '100%',
    backgroundColor: colors.neutral.black,
    border:          `1px solid ${colors.neutral.gray800}`,
    borderRadius:    borderRadius.md,
    color:           colors.neutral.white,
    padding:         spacing.sm,
    fontSize:        typography.fontSize.sm,
    fontFamily:      typography.fontFamily.body,
    resize:          'vertical',
    outline:         'none',
    boxSizing:       'border-box',
  };
}
